/**
 * PastDueList — annual past-due escalation.
 *
 * Per modules digest §RSM timeline:
 *   Aug 1 — rent due (field 126 / 128)
 *   Sep 1 — past-due notice mailed + $25 late fee added to field 157
 *   Oct 1 — final notice by registered mail
 *   ~January — drilling authorized (Retail Supervisor)
 *
 * Surfaces boxes with Field 133 (balance) > 0. Allows officer to:
 *   - Create RTM lost-key fee task ($75/key) when keys-missing flag (field 801) is set.
 *   - Mark registered-mail notice sent (SessionEvent).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Mail, AlertTriangle, KeyRound } from 'lucide-react';
import {
  DataGrid,
  Pill,
  Button,
  EntityIcon,
} from '@/components/primitives';
import type { DataGridColumn } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import type { SdbBoxRow } from './types';

const DEV_PAST_DUE: ReadonlyArray<SdbBoxRow> = import.meta.env.DEV
  ? [
      {
        acctId: 'SDB-0101',
        branchCode: '1',
        boxNumber: '0101',
        boxSize: '3x5',
        rentAmount: 40,
        nextDueDate: '2025-08-01',
        balance: 40,
        lateFee: 25,
        keyDeposit: 0,
        keysMissing: false,
        vacant: false,
        customerName: 'Brown, Mary',
        cifId: 'CIF-1011',
      },
      {
        acctId: 'SDB-0575',
        branchCode: '2',
        boxNumber: '0575',
        boxSize: '5x10',
        rentAmount: 85,
        nextDueDate: '2025-08-01',
        balance: 85,
        lateFee: 25,
        keyDeposit: 20,
        keysMissing: true,
        vacant: false,
        customerName: 'Garcia, Luis',
        cifId: 'CIF-2077',
      },
    ]
  : [];

export function PastDueList(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [working, setWorking] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const markRegisteredMailSent = async (box: SdbBoxRow): Promise<void> => {
    if (!user) return;
    setWorking((w) => ({ ...w, [box.acctId]: true }));
    try {
      await apiClient.retail.events.write({
        eventType: 'sdb-registered-mail-sent',
        eventCategory: 'compliance',
        severity: 'info',
        actor: user.upn,
        payloadJson: JSON.stringify({ acctId: box.acctId, branchCode: box.branchCode }),
      });
      setNotice(`Registered mail notice logged for ${box.acctId}.`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Failed to log notice.');
    } finally {
      setWorking((w) => ({ ...w, [box.acctId]: false }));
    }
  };

  const createLostKeyTask = async (box: SdbBoxRow): Promise<void> => {
    if (!user) return;
    setWorking((w) => ({ ...w, [box.acctId]: true }));
    try {
      await apiClient.retail.tasks.create({
        title: `SDB lost-key fee · box ${box.acctId}`,
        type: 'sdb-lost-key-fee',
        category: 'compliance',
        priority: 'Normal',
        assignedTo: user.upn,
        description: `$75 per missing key. Charge to customer on next access or close-out.`,
        severity: 'warn',
        autoCreated: false,
      });
      setNotice(`Lost-key task created for ${box.acctId}.`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Failed to create task.');
    } finally {
      setWorking((w) => ({ ...w, [box.acctId]: false }));
    }
  };

  const columns: DataGridColumn<SdbBoxRow>[] = [
    {
      key: 'box',
      label: 'Box',
      render: (r) => (
        <span className="rsm-cell--acct">
          <EntityIcon type="safedeposit" code="SD" />
          <span>
            <strong>{r.acctId}</strong>
            <div className="rsm-cell--sub">{r.customerName ?? ''}</div>
          </span>
        </span>
      ),
    },
    { key: 'branch', label: 'Branch', render: (r) => r.branchCode },
    {
      key: 'balance',
      label: 'Balance',
      numeric: true,
      render: (r) =>
        r.balance.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
    },
    {
      key: 'lateFee',
      label: 'Late fee',
      numeric: true,
      render: (r) =>
        r.lateFee.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
    },
    { key: 'nextDue', label: 'Due', render: (r) => r.nextDueDate.slice(0, 10) },
    {
      key: 'flags',
      label: 'Flags',
      render: (r) => (
        <span style={{ display: 'inline-flex', gap: 'var(--sp-3)' }}>
          {r.keysMissing && (
            <Pill variant="err" icon={<KeyRound size={12} />}>
              Keys missing
            </Pill>
          )}
          {r.lateFee > 0 && <Pill variant="warn">Late fee</Pill>}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <span style={{ display: 'inline-flex', gap: 'var(--sp-3)' }}>
          <Button
            variant="ghost"
            size="sm"
            startIcon={<Mail size={12} />}
            disabled={working[r.acctId]}
            onClick={() => void markRegisteredMailSent(r)}
          >
            Sent
          </Button>
          {r.keysMissing && (
            <Button
              variant="ghost"
              size="sm"
              startIcon={<KeyRound size={12} />}
              disabled={working[r.acctId]}
              onClick={() => void createLostKeyTask(r)}
            >
              Fee task
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="rsm-banner rsm-banner--warn" role="note">
        <AlertTriangle size={14} className="rsm-inline-icon" />
        <div>
          Escalation timeline: <strong>9/1</strong> notice mailed + $25 late fee, <strong>10/1</strong>{' '}
          registered mail, <strong>~January</strong> drilling authorized (Retail Supervisor).
          Drilled boxes hand off to RCC for escheatment.
        </div>
      </div>

      {notice && (
        <div className="rsm-banner rsm-banner--info" role="status">
          {notice}
        </div>
      )}

      <DataGrid<SdbBoxRow>
        columns={columns}
        rows={[...DEV_PAST_DUE]}
        rowKey={(r) => r.acctId}
        onRowAction={(r) => navigate(`box/${r.acctId}`)}
        emptyMessage="No past-due boxes."
      />
    </div>
  );
}
